export const CLM_ENVIRONMENTS = ["local", "sandbox", "test", "prod"] as const;
export type ClmEnvironment = (typeof CLM_ENVIRONMENTS)[number];

export const MODULE_IDS = [
  "dashboard",
  "admin",
  "member",
  "financial",
  "industry-resource-directory",
  "jobs",
  "ucc",
  "events",
  "courses",
  "compliance",
  "documents",
  "search",
  // 011-CLMS-237: CMS public read and session hand-off; its admin manifest is
  // kept out of MODULE_MANIFESTS (no sidebar or readiness-registry entry).
  "cms",
] as const;
export type ModuleId = (typeof MODULE_IDS)[number];

export type ApiBackedModuleId = Exclude<
  ModuleId,
  "dashboard" | "admin" | "member" | "industry-resource-directory" | "jobs"
>;

export const API_BACKED_MODULE_IDS = [
  "ucc",
  "events",
  "courses",
  "compliance",
  "financial",
  "documents",
  "search",
  "cms",
] as const satisfies readonly ApiBackedModuleId[];

export const RUNTIME_FEATURE_FLAG_KEYS = [
  "eventsModuleCandidate",
  "uccModuleEnabled",
  "cmsModuleEnabled",
] as const;
export type RuntimeFeatureFlagKey = (typeof RUNTIME_FEATURE_FLAG_KEYS)[number];

export const AUTH_REALM_IDS = ["admin"] as const;
export type AuthRealmId = (typeof AUTH_REALM_IDS)[number];

export interface AdminAuthRealmAuthorizationClaims {
  appRoleClaimName: string;
  requiredAppRoleValue: string;
}

export interface AdminAuthRealmConfig {
  spaOrigin: string;
  authBaseUrl: string;
  cognitoIssuer: string;
  cognitoJwksEndpoint: string;
  cognitoClientId: string;
  authCallbackUrl: string;
  authLogoutUrl: string;
  identityProvider: "entra-saml";
  samlProviderName: string;
  authorizationClaims: AdminAuthRealmAuthorizationClaims;
}

export interface AuthRealmsConfig {
  admin?: AdminAuthRealmConfig;
}

export type RoleClaim =
  | "anonymous"
  | "authenticated"
  | "owner"
  | "super_admin"
  | "admin"
  | "admin_support_staff"
  | "membership_coordinator"
  | "courses_admin"
  | "financial_controller"
  | "content_manager"
  | "member"
  | "financial:read"
  | "directory:read"
  | "jobs:read"
  | "ucc:read"
  | "events:read"
  | "courses:view"
  | "courses:manage"
  | "courses:extend"
  | "compliance:read"
  | "documents:read"
  | "search:read";

export interface UserProfile {
  sub: string;
  email: string;
  firstName?: string;
  lastName?: string;
  imisId?: string;
  memberType?: string;
  personId?: number;
  displayName?: string;
  roles?: RoleClaim[];
}

export type ActingSessionStatus = "starting" | "active" | "returning" | "ended" | "blocked";

export interface AdminActingSession {
  actingSessionId: string;
  actualUser: UserProfile;
  effectiveUser: UserProfile;
  actualUserRoles: RoleClaim[];
  effectiveUserRoles: RoleClaim[];
  startedAt: string;
  startedFromRoute: string;
  returnRoute: string;
  auditCorrelationId: string;
  status: ActingSessionStatus;
}

/**
 * 006-6 member authorization standing (standing contract §6.1), carried on
 * `/auth/me` as a standard-session UI hint only — never benefit authority.
 * The API denies server-side; the shell only decides what to show.
 */
export const MEMBER_STANDINGS = [
  "normal",
  "pre_activation",
  "renewal_inactive",
  "blocked",
] as const;
export type MemberStanding = (typeof MEMBER_STANDINGS)[number];

export interface AuthenticatedSession {
  authenticated: boolean;
  user?: UserProfile;
  roleClaims: RoleClaim[];
  accessTokenExpiresAt?: string;
  sessionCookieManagedByAuthService: boolean;
  actingSession?: AdminActingSession;
  /** Absent on older auth builds (and unauthenticated sessions) → normal shell. */
  standing?: MemberStanding;
}

/**
 * CLMS-117 registration checkout configuration. Everything here is optional
 * and fail-safe: a missing/empty publishable key means the payment step is
 * never rendered (the deferred surface shows instead). The Stripe publishable
 * key is public client-side material by design — never a secret. Poll values
 * bound the post-confirmation status polling per the registration checkout
 * UI contract (defaults: 5-second interval, 2-minute window).
 */
export interface RegistrationPaymentsConfig {
  stripePublishableKey?: string;
  statusPollIntervalSeconds?: number;
  statusPollWindowSeconds?: number;
}

export interface RuntimeConfig {
  environment: ClmEnvironment;
  appBaseUrl: string;
  authBaseUrl: string;
  authProviderTopology?: "cognito-auth-service-bff";
  cognitoIssuer?: string;
  cognitoJwksEndpoint?: string;
  cognitoClientId?: string;
  authCallbackUrl?: string;
  authLogoutUrl?: string;
  legacyAuthRedirectAllowed?: boolean;
  trackerBaseUrl?: string;
  trackerSsoSource?: "clm-core";
  /** Wiki base URL for the mandatory global-footer Wiki link (FR-041). */
  wikiBaseUrl: string;
  /**
   * Moodle LMS origin (`courses-{env}.theclm.org`; prod `courses.theclm.org`)
   * for the staff "Moodle administration" hand-off (004-6 FR-025, D19). Not
   * the courses API (`apiBaseUrls.courses`).
   */
  moodleBaseUrl: string;
  /**
   * Stage media CloudFront origin (011-CLMS-241 R13/R14): CMS images and videos load from it.
   * The CMS origin is trusted only in local builds; a deployed stage whose config omits this
   * renders no file.
   */
  mediaBaseUrl?: string;
  authRealms?: AuthRealmsConfig;
  apiBaseUrls: Record<ApiBackedModuleId, string>;
  buildSha: string;
  buildTime: string;
  featureFlags?: Partial<Record<RuntimeFeatureFlagKey, boolean>>;
  registrationPayments?: RegistrationPaymentsConfig;
}

export type TrackerEntitlementStatus = "active" | "inactive" | "expired" | "revoked" | "unknown";
export type TrackerEntitlementBasis = "company-managed-subscription" | "individual-subscription";

export interface TrackerEntitlementDecision {
  status: TrackerEntitlementStatus;
  decisionCorrelationId: string;
  /**
   * Traceability tag identifying the epic or story that produced this
   * entitlement decision (for example "CLMS-97"). This is auditing metadata,
   * not an authorization input, so it is intentionally an open string.
   */
  producerEpicOrStory: string;
  entitlementBasis?: TrackerEntitlementBasis;
  expiresAt?: string;
}

export interface TrackerAccessDecision {
  decision: "allow" | "deny";
  sourceApplication: string;
  authenticatedUser?: UserProfile;
  entitlement?: TrackerEntitlementDecision;
  failureReason?:
    | "unauthenticated"
    | "invalid-source"
    | "missing-entitlement"
    | "inactive-entitlement"
    | "unverifiable-entitlement";
  noAccessMessageKey?: "tracker.noAccess";
  decisionCorrelationId: string;
}

export type ApiContractStatus =
  | "not-ready"
  | "fixture-approved"
  | "contract-checked-in"
  | "sandbox-api-ready";
export type ProductConfirmationStatus = "pending" | "confirmed" | "blocked";
export type ApiEvidenceStatus = "none" | "fixture" | "openapi" | "sandbox-api";
export type ModuleImplementationStatus = "blocked" | "plan-ready" | "implementation-ready";

export interface ModuleReadinessGate {
  moduleId: ModuleId;
  jiraStory: string;
  backendEpic: string;
  productConfirmation: ProductConfirmationStatus;
  apiEvidence: ApiEvidenceStatus;
  implementationStatus: ModuleImplementationStatus;
  notes?: string;
}

/** A delivered entry surface within a module, rendered as a landing tile. */
export interface ModuleDeliveredSurface {
  id: string;
  title: string;
  description: string;
  route: `/${string}`;
  icon?: string;
  /** Role claims admitted to this surface; absent means the module's requiredRoles apply. */
  requiredRoles?: readonly RoleClaim[];
  /** "literal" disables hasRole expansions (e.g. admin ⇒ owner/super_admin); defaults to "expanded". */
  roleMatch?: "expanded" | "literal";
}

export interface ModuleManifest {
  moduleId: ModuleId;
  routeBase: `/${string}`;
  displayName: string;
  requiredRoles: RoleClaim[];
  navIcon: string;
  lazyEntryPoint: string;
  apiContractStatus: ApiContractStatus;
  readinessGate: ModuleReadinessGate;
  futureHostAlias?: string | null;
  protected: boolean;
  navigationGroup: "core" | "admin" | "member" | "modules" | "utility";
  /** Realms whose router serves the module. Absent: admin-group modules serve on the admin realm only, every other module on the standard realm only. */
  realms?: readonly ("standard" | "admin")[];
  /** "literal" matches requiredRoles without hasRole's admin ⇒ owner/super_admin expansion, for modules whose server gates are literal operator claims; defaults to "expanded". */
  roleMatch?: "expanded" | "literal";
  /** Delivered entry surfaces rendered as landing tiles; when absent, the module contributes no tiles (fail-closed, no routeBase fallback). */
  deliveredSurfaces?: readonly ModuleDeliveredSurface[];
  /** Runtime feature flag that must be `true` for the admin landing to list the module (absent: no flag). */
  featureFlag?: RuntimeFeatureFlagKey;
}

export interface RouteDefinition {
  id: string;
  path: `/${string}` | "/";
  label: string;
  public: boolean;
  moduleId?: ModuleId;
  requiredRoles: RoleClaim[];
  metadata?: {
    allowWhenUnauthenticated?: boolean;
    futureHostAlias?: string | null;
    denyByDefaultReason?: string;
  };
}

export type ShapeMappingDecision =
  | "preserve"
  | "rename"
  | "split"
  | "merge"
  | "normalize"
  | "drop"
  | "target-only";

export interface LegacyShapeMapEntry {
  moduleId: ModuleId;
  shapeName: string;
  targetProperty: string;
  legacySourcePath: string;
  legacyProperty?: string;
  legacyTypeOrSemantics: string;
  targetType: string;
  mappingDecision: ShapeMappingDecision;
  decisionRationale: string;
  contractEvidence: string;
}

export interface IconographyAsset {
  semanticUse: string;
  sourceType: "business-source-svg" | "tracker-svg" | "carbon" | "lucide" | "custom-approved";
  sourcePathOrName: string;
  accessibilityLabel: string;
  approvalStatus: "approved" | "needs-source-asset" | "needs-product-review";
}
